import { BrowserMultiFormatReader, type DecodeHintType } from '@zxing/library';
import * as React from 'react';

export interface UseBarcodeScannerOptions {
  /**
   * Called with the decoded text the first time a code is read for this mount.
   * Subsequent reads from the same mounted scanner are ignored — re-mount
   * (e.g. close + reopen the dialog) to scan again.
   */
  onResult?: (text: string) => void;
  /**
   * Called when the camera itself can't be accessed or started (permission
   * denied, no device, insecure context). NOT called for the per-frame
   * "nothing decoded yet" misses that happen constantly while scanning.
   */
  onError?: (error: unknown) => void;
  constraints?: MediaStreamConstraints;
  timeBetweenDecodingAttempts?: number;
  hints?: Map<DecodeHintType, unknown>;
}

const DEFAULT_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: { facingMode: 'environment' },
};

/**
 * Framework-agnostic camera barcode/QR scanner built on `@zxing/library`.
 * Attach the returned `ref` to a `<video>` element; the camera starts when the
 * element mounts and is released on unmount.
 *
 * Ported from artic-hub's `useBarcodeScanner`, with two fixes: the latest
 * callbacks are read from a ref (so the camera isn't restarted on every render
 * when an inline `onResult` changes), and a failed `decodeFromConstraints`
 * promise is caught and surfaced via `onError` instead of going unhandled.
 */
export function useBarcodeScanner({
  onResult,
  onError,
  constraints,
  timeBetweenDecodingAttempts = 300,
  hints,
}: UseBarcodeScannerOptions = {}) {
  const ref = React.useRef<HTMLVideoElement>(null);

  // Hold the latest options without re-running the camera-start effect.
  const optionsRef = React.useRef({ onResult, onError, constraints });
  optionsRef.current = { onResult, onError, constraints };

  const reader = React.useMemo(() => {
    const instance = new BrowserMultiFormatReader(hints);
    instance.timeBetweenDecodingAttempts = timeBetweenDecodingAttempts;
    return instance;
  }, [hints, timeBetweenDecodingAttempts]);

  React.useEffect(() => {
    const video = ref.current;
    if (!video) return;

    let settled = false;
    void reader
      .decodeFromConstraints(
        optionsRef.current.constraints ?? DEFAULT_CONSTRAINTS,
        video,
        (result) => {
          if (result && !settled) {
            settled = true;
            optionsRef.current.onResult?.(result.getText());
          }
          // The callback's `error` arg is a per-frame NotFoundException while
          // scanning — that's expected, so it is deliberately ignored here.
        },
      )
      .catch((error: unknown) => {
        optionsRef.current.onError?.(error);
      });

    return () => {
      reader.reset();
    };
  }, [reader]);

  return { ref };
}
